import { useEffect, useState } from "react";

import { APICallStatus } from "src/contentful";

import type { Request as TesseractRequest } from "@atlassian/tesseract-runtime-globals";

import {
    BxpExpressServiceSupportedRegions,
    TesseractRuntimeSupportedRegions
} from "@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client";

export type UserLocale = string;
export const DEFAULT_COUNTRY_CODE: UserLocale = "US";

export interface LocationData {
    countryIsoCode: string;
    regionIsoCode: string;
}

export type CurrencyISOCode = "USD" | "JPY";
export const COUNTRY_CODE_TO_CURRENCY: Record<UserLocale, CurrencyISOCode> = {
    US: "USD",
    JP: "JPY"
};

export const getCurrencyFromUserLocale = (
    userLocale: UserLocale
): CurrencyISOCode => {
    // @ts-ignore
    return (
        COUNTRY_CODE_TO_CURRENCY[userLocale] ??
        COUNTRY_CODE_TO_CURRENCY[DEFAULT_COUNTRY_CODE]
    );
};

export async function fetchUserLocaleS2SWithLocationData(
    baseUrl: string,
    request: Request | TesseractRequest
): Promise<LocationData> {
    if (typeof window === "undefined") {
        const headers: Record<string, string> = {};

        //@ts-ignore
        const awsRegion: string = globalThis.ssrContext
            .awsRegion as TesseractRuntimeSupportedRegions;

        // S2S calls only work if the downstream service exists in the same region that our tesseract runtime is in
        const downStreamServiceExistsInSameTesseractRuntimeRegion: boolean =
            BxpExpressServiceSupportedRegions.has(awsRegion);

        if (!downStreamServiceExistsInSameTesseractRuntimeRegion) {
            console.log(
                "Downstream service does not exist in the same region as current tesseract region. Current tesseract region: " +
                    awsRegion
            );
            const fallbackLocale = await fetchUserLocale(baseUrl);
            return {
                countryIsoCode: fallbackLocale,
                regionIsoCode: ""
            };
        }

        headers["x-ssr-service-name"] = "bxp-express";
        //@ts-ignore
        const environmentType: string = globalThis.ssrContext.environmentType;
        if (environmentType === "dev") {
            // bxp-express service does not have any dev instances so when we're in dev, point to staging
            headers["x-ssr-service-env"] = "staging";
            headers["x-ssr-service-region"] = "us-east-1"; // Bxp-express service in staging only lives in us-east-1 or us-west-2
        }

        //@ts-ignore
        headers["X-Forwarded-For"] = request?.headers["x-forwarded-for"];

        return await fetch(`/location`, { headers: headers })
            .then((response) => {
                console.log(
                    "[GeoCurrency] S2S location fetch - response status:",
                    response.status
                );
                return response.json();
            })
            .then((json) => {
                console.log(
                    "[GeoCurrency] S2S location fetch - response JSON:",
                    json.countryIsoCode,
                    json.regionIsoCode
                );
                return {
                    countryIsoCode:
                        (json.countryIsoCode as UserLocale) ||
                        DEFAULT_COUNTRY_CODE,
                    regionIsoCode: json.regionIsoCode || ""
                };
            })
            .catch(async (error) => {
                console.error(
                    "Error for fetchUserLocaleS2SWithLocationData:",
                    error
                ); // Log any errors
                // If there's an error with s2s call - fall back to original fetchUserLocale
                const fallbackLocale = await fetchUserLocale(baseUrl);
                return {
                    countryIsoCode: fallbackLocale,
                    regionIsoCode: ""
                };
            });
    } else {
        console.log(
            "[GeoCurrency] Window is undefined - falling back to client side location fetch"
        );
        const fallbackLocale = await fetchUserLocale(baseUrl);
        return {
            countryIsoCode: fallbackLocale,
            regionIsoCode: ""
        };
    }
}

export async function fetchUserLocaleS2S(
    baseUrl: string,
    request: Request | TesseractRequest
): Promise<UserLocale> {
    console.log("[GeoCurrency] fetchUserLocaleS2S - start");
    const locationData = await fetchUserLocaleS2SWithLocationData(
        baseUrl,
        request
    );
    console.log(
        "[GeoCurrency] fetchUserLocaleS2S - countryIsoCode:",
        locationData.countryIsoCode
    );
    return locationData.countryIsoCode;
}

export async function fetchUserLocale(baseUrl: string): Promise<UserLocale> {
    console.log("[GeoCurrency] fetchUserLocale - start");
    const locationData = await fetchLocationDataWithBaseUrl(baseUrl);
    console.log(
        "[GeoCurrency] fetchUserLocale - countryIsoCode:",
        locationData.countryIsoCode
    );
    return locationData.countryIsoCode;
}

export async function fetchLocationDataWithBaseUrl(
    baseUrl: string
): Promise<LocationData> {
    console.log(
        "[GeoCurrency] fetchLocationDataWithBaseUrl - fetching:",
        `${baseUrl}/location`
    );
    return await fetch(`${baseUrl}/location`)
        .then((response) => {
            console.log(
                "[GeoCurrency] fetchLocationDataWithBaseUrl - response status:",
                response.status
            );
            return response.json();
        })
        .then((json) => {
            console.log(
                "[GeoCurrency] fetchLocationDataWithBaseUrl - response JSON:",
                json.countryIsoCode,
                json.regionIsoCode
            );
            return {
                countryIsoCode:
                    (json.countryIsoCode as UserLocale) || DEFAULT_COUNTRY_CODE,
                regionIsoCode: json.regionIsoCode || ""
            };
        })
        .catch((error) => {
            console.error(
                "[GeoCurrency] fetchLocationDataWithBaseUrl - error:",
                error
            );
            return {
                countryIsoCode: DEFAULT_COUNTRY_CODE,
                regionIsoCode: ""
            };
        });
}

export function useGeoLocation(baseUrl: string) {
    const [status, setStatus] = useState<APICallStatus>("idle");
    const [countryCode, setCountryCode] = useState<UserLocale>();
    useEffect(() => {
        const fetchData = async () => {
            setStatus("fetching");
            const locale = await fetchUserLocale(baseUrl);
            setStatus("complete");
            setCountryCode(locale);
        };
        fetchData();
    }, []);
    return { status, countryCode };
}
